import React, { FC, useCallback, useEffect } from "react";
import PanelBackButton from "containers/RightPanel/components/PanelBackButton";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { KbArticleVariant } from "enums/KbArticle";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { KnowledgeDto } from "types/api/help";
import { OpenRightPanel } from "types/common/rightPanel";
import injectReducer from "../../../../reduxFolder/injectReducer";
import helpReducer, {
  getKnowledgeBaseHelpers,
} from "../../../../reduxFolder/reducers/help";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  helpSelectIsKbHelpersLoading,
  helpSelectKbHelpers,
} from "../../../../reduxFolder/selectors/helpSelectors";
import { authSelectLocale } from "../../../../reduxFolder/selectors/globalSelectors";
import { Answer, HelpContent, Spinner, Title } from "./HelpPanel.styles";

interface HelpPanelProps {
  currentLocale: string;
  openRightPanel: OpenRightPanel;
  panelData: any;
  kbHelpersList: any;
  isKbHelpersLoading: any;
  getKnowledgeBaseHelpers: ({
    currentLocale,
  }: KnowledgeDto) => Promise<unknown>;
}

const HelpPanel: FC<HelpPanelProps> = React.memo(
  ({
    panelData,
    openRightPanel,
    kbHelpersList,
    isKbHelpersLoading,
    getKnowledgeBaseHelpers,
    currentLocale,
  }) => {
    const readOnly = panelData.get("readOnly");
    const hideBackButton = panelData.get("hideBackButton");
    const returnPanelType = panelData.get("returnPanelType");
    const returnPanelData = panelData.get("returnPanelData");
    const title = panelData.get("headerTitle")
      ? panelData.get("headerTitle")
      : "FAQ";
    const showHelpersCode = panelData.get("showHelpersCode")
      ? panelData.get("showHelpersCode")
      : "packopt";

    const filteredKbHelpersList = kbHelpersList.filter(
      (faqItem: any) => faqItem.get("code") === showHelpersCode,
    );
    const renderHelperAnswer = useCallback(
      (faqItem: any) => (
        <Answer
          key={faqItem.get("code")}
          html={faqItem.get("content")}
          variant={KbArticleVariant.Embedded}
        />
      ),
      [],
    );

    const fetchKnowledgeBaseHelpers = useCallback(async () => {
      try {
        await getKnowledgeBaseHelpers({ currentLocale });
      } catch (e) {
        console.error(e);
      }
    }, [currentLocale, getKnowledgeBaseHelpers]);

    useEffect(() => {
      fetchKnowledgeBaseHelpers();
    }, [fetchKnowledgeBaseHelpers]);

    const handleBack = useCallback(
      () => {
        if (returnPanelType) {
          openRightPanel(returnPanelType, returnPanelData);
          return;
        }

        openRightPanel(
          "PACK_OPTIONS",
          panelData
            .set("preventReLoading", true)
            .set("returnScreen", "STORAGE_ITEM")
            .set("readOnly", readOnly),
        );
      },
      [panelData, openRightPanel, readOnly, returnPanelData, returnPanelType],
    );

    return (
      <ContentWrapper>
        <Heading $isBordered>
          {!hideBackButton && (
            <PanelBackButton onClick={handleBack} />
          )}
          <Title>{title}</Title>
        </Heading>
        <Spinner isActive={isKbHelpersLoading && !kbHelpersList.size} />
        {!!filteredKbHelpersList.size && (
          <HelpContent>
            {filteredKbHelpersList.map(renderHelperAnswer)}
          </HelpContent>
        )}
      </ContentWrapper>
    );
  },
);

const withReducer = injectReducer({
  key: "help",
  reducer: helpReducer,
});

const withConnect = connect(
  createStructuredSelector({
    kbHelpersList: helpSelectKbHelpers,
    isKbHelpersLoading: helpSelectIsKbHelpersLoading,
    currentLocale: authSelectLocale,
  }),
  {
    getKnowledgeBaseHelpers,
    openRightPanel,
  },
);

const enhance = compose(withReducer, withConnect);

export default enhance(HelpPanel);
